import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  getWorkflowStatusesMasterDataAsync,
  WorkflowStatus
} from './workflowStatusSlice';

interface WorkflowStatusParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

const sortOptions = [
  { value: 'id', text: 'createdDate' },
  { value: 'name', text: 'name' }
];

export default function WorkflowStatusView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { masterData: workflowStatuses, count } = useSelector(
    (state: RootState) => state.workflowStatuses
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] =
    useState<WorkflowStatus | null>(null);
  const [workflowStatusParams, setWorkflowStatusParams] =
    useState<WorkflowStatusParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'id',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingWorkflowStatus(workflowStatusParams);
  }, []);

  function loadingWorkflowStatus(params: WorkflowStatusParamProps) {
    dispatch(getWorkflowStatusesMasterDataAsync(params));
    setWorkflowStatusParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingWorkflowStatus({
      ...workflowStatusParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, workflowStatus?: WorkflowStatus) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && workflowStatus) {
        setSelectedWorkflowStatus(workflowStatus);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedWorkflowStatus?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`workflow-statuses/${selectedWorkflowStatus?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('masterData.workflowStatus.actions.edit')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('masterData.workflowStatus.actions.delete')}
                secondary={t('projectCard.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('masterData.workflowStatus.title'),
          masterDataRef: 'workflow-statuses'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('masterData.workflowStatus.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('masterData.workflowStatus.title')}
        params={workflowStatusParams}
        sortOptions={sortOptions.map((s) => ({ ...s, text: t(`masterData.sortOptions.${s.text}`) }))}
        loadingElement={loadingWorkflowStatus}
      />
      {workflowStatuses.map((workflowStatus) => {
        return (
          <Card className={classes.card} key={workflowStatus.id}>
            <div className={classes.cardContainer}>
              <Typography>{workflowStatus.name}</Typography>
            </div>
            <div>
              <IconButton
                size="small"
                onClick={toggleDrawer(true, workflowStatus)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / workflowStatusParams.limit)}
          shape="rounded"
          page={workflowStatusParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`workflow-statuses/create`}
          variant="extended"
          color="primary"
          aria-label={t('masterData.workflowStatus.actions.newAria') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('masterData.workflowStatus.actions.new')}
        </Fab>
      </Zoom>
    </div>
  );
}
